import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/dist/query";

import {
  GetPracticesRequest,
  CreatePracticeRequest,
  UpdatePracticeRequest,
  GetPracticeStatsRequest,
  VerifyTenantRequest,
} from "api/types/requests";
import {
  CreatePracticeResponse,
  UpdatePracticeResponse,
  PracticeStatsResponse,
  VerifyTenantResponse,
} from "api/types/responses";
import QueryTags from "api/types/QueryTags";
import Practice from "api/types/models/practice";

const practiceEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      {},
      FetchBaseQueryMeta
    >,
    QueryTags,
    "data"
  >,
) => ({
  getPractices: builder.query<Practice[], GetPracticesRequest>({
    query: ({ filtering, sorting, includeLocations = false }) => ({
      url: "/practices",
      params: {
        ...(filtering?.status && { status: filtering.status }),
        ...(filtering?.search_term && { search_term: filtering.search_term }),
        ...(sorting?.sort_by && { sort_by: sorting.sort_by }),
        include_locations: includeLocations,
      },
    }),
    providesTags: ["Practices"],
  }),

  getPractice: builder.query<Practice, number>({
    query: (id) => `/practices/${id}`,
    providesTags: ["Practice"],
  }),

  createPractice: builder.mutation<
    CreatePracticeResponse,
    CreatePracticeRequest
  >({
    query: (body) => ({
      url: "/practices",
      method: "post",
      body,
    }),
    invalidatesTags: ["Practices"],
  }),

  verifyTenant: builder.mutation<VerifyTenantResponse, VerifyTenantRequest>({
    query: (body) => ({
      url: "/practices/verify_tenant",
      method: "post",
      body,
    }),
  }),

  updatePractice: builder.mutation<
    UpdatePracticeResponse,
    { id: number; body: UpdatePracticeRequest }
  >({
    query: ({ id, body }) => ({
      url: `/practices/${id}`,
      method: "put",
      body,
    }),
    invalidatesTags: ["Practices", "Practice"],
  }),

  deletePractice: builder.mutation<void, number>({
    query: (id) => ({
      url: `/practices/${id}`,
      method: "delete",
    }),
    invalidatesTags: ["Practices", "Practice"],
  }),

  getPracticeStats: builder.query<
    PracticeStatsResponse,
    { filters: GetPracticeStatsRequest }
  >({
    query: ({ filters }) => ({
      url: "/practices/stats",
      params: {
        ...(filters.location_uuid && { location_uuid: filters.location_uuid }),
        start_date: filters.start_date,
        end_date: filters.end_date,
      },
    }),
  }),

  switchPractice: builder.mutation<void, number>({
    query: (id) => ({
      url: `/practices/${id}/switch`,
      method: "post",
    }),
  }),

  endSwitchPractice: builder.mutation<void, void>({
    query: () => ({
      url: "/practices/switch",
      method: "delete",
    }),
  }),
});

export default practiceEndpoints;
